<script setup lang="ts">
import { Plus, EditPencil, Trash } from "@iconoir/vue";
import { useList } from "~/composables/use-list";
import { useCrud } from "~/composables/use-crud";
import { useFormat } from "~/composables/use-format";
import type { WorkspacePlan } from "@artech-global/types";
import ListPage from "~/components/templates/ListPage.vue";
import ListEmpty from "~/components/templates/ListEmpty.vue";
import WorkspacePlanFormModal from "./WorkspacePlanFormModal.vue";
import { AdminPermission } from "~~/schemas/admin-role";

defineOptions({ name: "WorkspacePlan" });

const { t } = useI18n();
const { d } = useFormat();
const toast = useToast();
const alert = useAlert();
const { can } = useAccess();
const { set: setTitle } = usePageTitle();

const defaultFilter = {
  keyword: "",
  status: "all",
  page: 1,
  pageSize: 20,
};

const {
  firstLoading,
  listLoading,
  load,
  itemCount,
  pageCount,
  items,
  filter,
  isEmpty,
  isSearchEmpty,
  search,
  searchDebounced,
} = useList<WorkspacePlan, typeof defaultFilter>({
  endpoint: "/workspace-plans",
  defaultFilter,
});

const { del, errorMsg } = useCrud<WorkspacePlan>({
  endpoint: "/workspace-plans",
});

const formRef = ref<InstanceType<typeof WorkspacePlanFormModal> | null>(null);

const statusOptions = computed(() => [
  { value: "all", label: t("workspacePlan.statusAll") },
  { value: "deleted", label: t("workspacePlan.statusDeleted") },
]);

function openCreate() {
  formRef.value?.open();
}

function openEdit(row: WorkspacePlan) {
  formRef.value?.open(row.id as string);
}

async function confirmDelete(row: WorkspacePlan) {
  const ok = await alert.negative({
    title: t("workspacePlan.deleteConfirmTitle"),
    description: t("workspacePlan.deleteConfirmBody", { name: row.name }),
    confirmLabel: t("action.delete"),
    cancelLabel: t("action.cancel"),
  });
  if (!ok) return;

  try {
    await del(row.id as any);
    toast.success({ title: t("message.deleteSuccess") });
    const nextItemCount = Math.max(0, itemCount.value - 1);
    const nextPageCount = Math.max(1, Math.ceil(nextItemCount / filter.pageSize));
    if (Number(filter.page) > nextPageCount) {
      filter.page = nextPageCount;
    }
    await load();
  } catch {
    if (errorMsg.value) toast.error({ title: errorMsg.value });
  }
}

function formatPrice(plan: WorkspacePlan) {
  if (!plan.prices?.length) return "—";
  return plan.prices
    .map((p) => `${p.billingCycle}: ${p.price}`)
    .join(" · ");
}

async function onSaved() {
  await load();
}

onMounted(async () => {
  setTitle(t("workspacePlan.title"));
  await load();
});

watch(
  () => filter.keyword,
  () => {
    searchDebounced();
  },
);

watch(
  () => filter.status,
  () => {
    search();
  },
);

watch(
  () => filter.page,
  () => {
    load();
  },
);
</script>

<template>
  <ListPage
    :title="t('workspacePlan.title')"
    :first-loading="firstLoading"
    :is-empty="isEmpty"
    :is-search-empty="isSearchEmpty"
  >
    <template #status>
      {{ listLoading ? t("ui.loading") : `${itemCount} ${t('workspacePlan.itemsSuffix')}` }}
    </template>

    <template #actions>
      <Button
        v-if="can(AdminPermission.WorkspacePlansWrite)"
        variant="primary"
        size="small"
        @click="openCreate"
      >
        <Plus class="icon" />
        {{ t("workspacePlan.add") }}
      </Button>
    </template>

    <template #empty>
      <ListEmpty
        :title="t('workspacePlan.emptyTitle')"
        :subtitle="t('workspacePlan.emptySubtitle')"
      >
        <template #action>
          <Button v-if="can(AdminPermission.WorkspacePlansWrite)" variant="primary" size="small" @click="openCreate">
            <Plus class="icon" />
            {{ t("workspacePlan.add") }}
          </Button>
        </template>
      </ListEmpty>
    </template>

    <template #search-empty>
      <ListEmpty
        :title="t('workspacePlan.emptySearchTitle')"
        :subtitle="t('workspacePlan.emptySearchSubtitle')"
      />
    </template>

    <template #filter-left>
      <FormSelect
        v-model="filter.status"
        :options="statusOptions"
        size="sm"
        class="w-full md:w-1/2"
      />
    </template>

    <template #filter-right>
      <FormSearch
        v-model="filter.keyword"
        :placeholder="t('field.search')"
        size="sm"
        class="w-full md:w-1/2"
      />
    </template>

    <template #default>
      <DataTable
        :columns="[
          { key: 'name', label: t('field.name'), class: 'md:min-w-[200px]' },
          { key: 'key', label: t('field.key'), class: 'min-w-[112px] hidden md:table-cell' },
          { key: 'type', label: t('field.type'), class: 'min-w-[112px] hidden md:table-cell' },
          { key: 'prices', label: t('workspacePlan.pricingTitle'), class: 'min-w-[160px] hidden md:table-cell' },
          { key: 'createTime', label: t('field.createTime'), class: 'min-w-[160px] hidden md:table-cell' },
          { key: 'actions', align: 'right', label: t('ui.action'), class: 'min-w-[80px]' },
        ]"
        :items="items"
      >
        <template #cell-name="{ row }">
          <span class="font-semibold">{{ row.name }}</span>
        </template>

        <template #cell-type="{ row }">
          <Badge intent="neutral" emphasis="medium" size="small">
            {{ row.type || "—" }}
          </Badge>
        </template>

        <template #cell-prices="{ row }">
          <span class="text-body-small-tertiary">{{ formatPrice(row) }}</span>
        </template>

        <template #cell-createTime="{ row }">
          {{ d(row.createTime, 'datetime') }}
        </template>

        <template #actions="{ row }">
          <div class="flex items-center justify-end gap-xsmall">
            <ButtonIcon
              v-if="can(AdminPermission.WorkspacePlansWrite)"
              variant="neutral-flat"
              size="small"
              :title="t('action.edit')"
              :aria-label="t('action.edit')"
              @click.stop="openEdit(row)"
            >
              <EditPencil class="icon" />
            </ButtonIcon>

            <ButtonIcon
              v-if="can(AdminPermission.WorkspacePlansWrite)"
              variant="neutral-flat"
              size="small"
              :title="t('action.delete')"
              :aria-label="t('action.delete')"
              @click.stop="confirmDelete(row)"
            >
              <Trash class="icon" />
            </ButtonIcon>
          </div>
        </template>
      </DataTable>

      <Pagination
        v-if="pageCount > 1"
        v-model:page="filter.page"
        :count="itemCount"
        :page-size="filter.pageSize"
        class="mt-medium"
      />
    </template>
  </ListPage>

  <WorkspacePlanFormModal ref="formRef" @saved="onSaved" />
</template>
